import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { Grid, Divider, Col, Row } from '@components/Grid';
import Button from '@components/Button';
import { addToBasket, emptyBasket, setBasketItem } from '@actions/basket';
import { bindActionCreators } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
    getBasket,
    getPublicProductDiscount,
    getProductDiscountPrice,
    getProductDiscountName,
    getProductDiscountAccountType,
} from '@helpers/store';
import { makePutRequest, makePostRequest } from '@helpers/requests';
import { BASKET_ADD, BASKET_CREATE } from '@helpers/api';
import Layout from '@components/Layout';
// import ReactPixel from 'react-facebook-pixel';
import SEO from '@components/SEO';
import Card from '@components/Card';
import styles from './styles.module.scss';
import ImageGallery from 'react-image-gallery';
import { graphql } from 'gatsby';
import { useSnackbar } from '@components/Snackbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProductCard from '@components/ProductCard';
import { shuffleArray } from '@helpers/index';
import { getCustomField, getCustomFieldValue } from '@helpers/customField';
import Text from '@components/Text';
import Img from 'gatsby-image';

const Product = ({
    data: {
        merlinWebsite: {
            store: {
                currency: { symbol: currencySymbol, code: currencyCode },
            },
        },
        merlinStoreProduct: product,
        allMerlinStoreProduct: { nodes: products },
        allMerlinAccountType: { nodes: accountTypes },
    },
}) => {
    const {
        _id: id,
        name,
        price,
        description,
        images,
        shortName,
        category,
        customFields,
    } = product;
    const dispatch = useDispatch();
    const [openSnackbar] = useSnackbar();
    const basket = useSelector(({ basket }) => basket ?? {});
    const basketProducts = getBasket(products, basket);
    const [quantity, setQuantity] = useState(
        basketProducts?.products?.find(({ productId }) => productId === id)?.quantity || 1
    );
    const discount = getPublicProductDiscount({
        price: product?.price,
        discounts: product?.discounts,
    });
    const discountName = getProductDiscountName({ price: product?.price, discount });
    const discountAccountType = getProductDiscountAccountType({ accountTypes, discount });

    // useEffect(() => {
    //     if (process.env.NODE_ENV === 'production') {
    //         ReactPixel.track('ViewContent', {
    //             content_type: 'product',
    //             content_ids: [id],
    //             content_name: name,
    //             currency: currencyCode,
    //             value: parseFloat(price / 100),
    //         });
    //     }
    // }, []);

    const handleAddToBasket = async () => {
        try {
            dispatch(addToBasket({ id, quantity }));

            const { data: basketData } = !!basket?.id
                ? await makePutRequest(BASKET_ADD(basket.id), {
                      productId: id,
                      quantity,
                  })
                : await makePostRequest(BASKET_CREATE, {
                      productId: id,
                      quantity,
                  });

            const newQuantity =
                basketData?.products?.find(({ productId }) => productId === id)?.quantity ??
                quantity;

            openSnackbar(`You successfully added ${name} to your basket.`);

            if (quantity !== newQuantity || !basket?.id) {
                dispatch(setBasketItem({ id, quantity: newQuantity, basketId: basketData?._id }));
            }

            // update quantity to ensure it's the same as API
            setQuantity(newQuantity);
        } catch (error) {
            if (error !== 'cancelled') {
                openSnackbar(
                    error?.errorMessage ?? 'An error occurred adding this item to your basket'
                );
                dispatch(emptyBasket());
            }
        }
    };

    const relatedProducts = useMemo(
        () =>
            shuffleArray(
                products
                    .filter(
                        product =>
                            product?._id !== id &&
                            product.category?._id === category?._id &&
                            product?.images[0]
                    )
                    .filter(x => !!x)
            ).slice(0, 3),
        []
    );

    const relatedBasketProducts = getBasket(relatedProducts, basket);

    const sliderImages = (images || [])?.map(({ image }) => ({
        original: image.childImageSharp.fluid.src,
        thumbnail: image.childImageSharp.fluid.src,
        originalClass: styles.sliderImage,
    }));

    const summary = getCustomField(customFields, 'summary');
    const videos = getCustomField(customFields, 'installation-url', data =>
        !!data?.length
            ? data?.filter(video => video.includes('https://') || video.includes('http://'))
            : null
    );
    const graphic = getCustomField(customFields, 'graphic');
    const ffl = getCustomField(customFields, 'ffl');

    const specificationTypes = [
        'suppressor-specification',
        'conversion-block-specification',
        'handgun-accessories-specification',
        'rifle-accessories-specification',
        'speed-loader-specification',
        'folding-stock-adapter-specification',
        'other-specification',
    ];

    const specification = {
        ...(customFields?.find(({ id }) => specificationTypes.some(type => type === id)) ?? {}),
        items: customFields
            ?.find(({ id }) => specificationTypes.some(type => type === id))
            ?.items?.map(item => ({
                ...item,
                value: getCustomFieldValue(item),
            })),
    };

    const [currentTab, setCurrentTab] = useState('description');

    const Description = () => {
        return (
            <Fragment>
                <Row center="xs" start="lg" className={styles.lowerSection}>
                    <Col xs={12} md={9} lg={6}>
                        <div className={styles.descriptionMain}>
                            <h2 className={styles.descriptionHeading}>
                                {summary?.label ?? 'Description'}
                            </h2>
                            {!!summary?.hint && <p>{summary?.hint}</p>}
                            <Divider margin={2} />
                            {!!summary?.value ? (
                                <div
                                    className={styles.description}
                                    dangerouslySetInnerHTML={{ __html: summary?.value }}
                                />
                            ) : (
                                <p>-</p>
                            )}
                        </div>
                        <Divider xsMargin={3} lgMargin={0} />
                    </Col>

                    {!!specification?.items?.length && (
                        <Col xs={12} md={9} lg={6}>
                            <div className={styles.descriptionMain}>
                                <h2>{specification?.label}</h2>
                                {!!specification?.hint && <p>{specification?.hint}</p>}

                                {specification?.items?.map(item => {
                                    return (
                                        <Fragment key={`${item?.label}_${item?.value}`}>
                                            <Divider margin={2} />
                                            {!!item && (
                                                <Row
                                                    middle="xs"
                                                    start="xs"
                                                    column="xs"
                                                    row="lg"
                                                    className={styles.specification}
                                                >
                                                    <Col lg={3} className={styles.property}>
                                                        {item?.label}
                                                    </Col>
                                                    <Col lg={9} className={styles.content}>
                                                        {item?.value ?? '-'}
                                                    </Col>
                                                </Row>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </div>
                        </Col>
                    )}
                </Row>
            </Fragment>
        );
    };

    const Installation = () => {
        if (!videos?.value) return null;

        return (
            <div>
                <h2 className={styles.installationHeading}>
                    {videos?.label ?? 'Installation Videos'}
                </h2>
                {!!videos?.hint && <p>{videos?.hint}</p>}
                <Row>
                    {videos?.value?.map(video => {
                        return (
                            <Col key={video} xs={12} md={6} className={styles.videocard}>
                                <iframe width="100%" height="300" src={video} />
                                <Divider />
                            </Col>
                        );
                    })}
                </Row>
            </div>
        );
    };

    return (
        <Layout>
            <SEO product={product} />

            <Grid>
                <Row center="xs" start="lg">
                    <Col xs={12} lg={6}>
                        {!!images && !!images.length && (
                            <ImageGallery
                                showFullscreenButton={false}
                                showPlayButton={false}
                                items={sliderImages}
                            />
                        )}
                    </Col>
                    <Col xs={12} lg={6}>
                        <Divider xsMargin={2} lgMargin={0} />
                        <div className={styles.content}>
                            <Card>
                                <Card.Content>
                                    <h2>{name}</h2>
                                    {/*<div className={styles.stars}>*/}
                                    {/*    {Array.from({ length: 5 }, (x, index) => (*/}
                                    {/*        <FontAwesomeIcon icon="star" className={styles.star} />*/}
                                    {/*    ))}*/}
                                    {/*</div>*/}
                                    <div className={styles.description}>{description}</div>
                                    <Divider margin={2} />
                                    {ffl?.value && (
                                        <div className={styles.ffl}>
                                            <Text status="warning">
                                                This item must be collected from an FFL dealer.
                                            </Text>
                                        </div>
                                    )}

                                    <Divider margin={2} />
                                    {!!discount && (
                                        <Fragment>
                                            <Divider margin={2} />
                                            <Row end="xs">
                                                <Col xs={12}>
                                                    <div className={styles.discount}>
                                                        {!!discountName && (
                                                            <span className={styles.discountName}>
                                                                {discountName}
                                                            </span>
                                                        )}
                                                        {!!discountAccountType && (
                                                            <span
                                                                className={
                                                                    styles.discountAccountType
                                                                }
                                                            >{`${discountAccountType?.name} Discount`}</span>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Fragment>
                                    )}

                                    <Row center="xs" end="lg">
                                        <Col xs={12}>
                                            <div
                                                className={`${styles.price} ${
                                                    !!discount ? styles.priceDiscounted : ''
                                                }`}
                                            >
                                                <span
                                                    className={styles.priceRetail}
                                                >{`${currencySymbol}${parseFloat(price / 100)
                                                    .toFixed(2)
                                                    .replace(/\.?0+$/, '')}`}</span>
                                                {!!discount && (
                                                    <span className={styles.priceDiscount}>
                                                        {`${currencySymbol}${parseFloat(
                                                            getProductDiscountPrice({
                                                                price,
                                                                discount,
                                                            }) / 100
                                                        ).toFixed(2)}`}
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Content>
                            </Card>
                            <Divider xsMargin={3} lgMargin={2} />
                            <div className={styles.variants}>
                                <div className={styles.basketItemRight}>
                                    <ul className={styles.basketItemQuantity}>
                                        <li
                                            className={`${
                                                styles.basketItemQuantityAction
                                            } ${quantity < 2 && styles.disabled}`}
                                            onClick={() => {
                                                quantity > 1 && setQuantity(quantity - 1);
                                            }}
                                        >
                                            <FontAwesomeIcon icon="minus" />
                                        </li>
                                        <li className={styles.basketItemQuantityNumber}>
                                            {quantity}
                                        </li>
                                        <li
                                            className={styles.basketItemQuantityAction}
                                            onClick={() => setQuantity(quantity + 1)}
                                        >
                                            <FontAwesomeIcon icon="plus" />
                                        </li>
                                    </ul>
                                </div>
                                <Button text="Add to cart" onClick={handleAddToBasket} />
                            </div>
                        </div>
                    </Col>
                </Row>
                <Divider margin={5} />
                <Row>
                    <Col xs={12}>
                        {!!graphic?.value?.image?.childImageSharp?.fluid && (
                            <Img fluid={graphic?.value?.image?.childImageSharp?.fluid} />
                        )}
                    </Col>
                </Row>
                <Divider margin={5} />
                {!!videos?.value?.length && (
                    <Fragment>
                        <Row className={styles.navigation}>
                            <Col xs={6}>
                                <Row
                                    center="xs"
                                    middle="xs"
                                    className={`${currentTab === 'description' &&
                                        `${styles.active}`} ${styles.heading}`}
                                >
                                    <Col xs={12} lg={8}>
                                        <h2 onClick={() => setCurrentTab('description')}>
                                            DESCRIPTION
                                        </h2>
                                    </Col>
                                </Row>
                            </Col>

                            <Col xs={6}>
                                <Row
                                    center="xs"
                                    middle="xs"
                                    className={`${currentTab === 'installation' &&
                                        `${styles.active}`} ${styles.heading} `}
                                >
                                    <Col xs={12} lg={8}>
                                        <h2 onClick={() => setCurrentTab('installation')}>
                                            INSTALLATIONS
                                        </h2>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Divider margin={5} />
                    </Fragment>
                )}

                {currentTab === 'installation' && <Installation />}
                {currentTab === 'description' && <Description />}

                <Divider />
                <Row center="xs" start="lg">
                    <Col xs={12}>
                        <Button text="Find a dealer" link="/dealers-locations" />
                    </Col>
                </Row>
                <Divider margin={5} />

                {!!relatedProducts?.length && (
                    <Fragment>
                        <Divider />
                        <Row>
                            <Col xs={12}>
                                <div className={styles.relatedMain}>
                                    <Divider margin={5} />
                                    <Divider margin={3} />
                                    <h2 className={styles.relatedHeading}>RELATED</h2>
                                    <Row>
                                        {relatedProducts.map(product => {
                                            return (
                                                <Col
                                                    key={product?._id}
                                                    xs={12}
                                                    md={6}
                                                    lg={4}
                                                    className={styles.card}
                                                >
                                                    <ProductCard
                                                        product={product}
                                                        basket={relatedBasketProducts}
                                                    />
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </Fragment>
                )}
            </Grid>
        </Layout>
    );
};

export default connect(
    ({ basket }) => ({ basketId: basket ? basket.id : null }),
    dispatch => bindActionCreators({ addToBasket }, dispatch)
)(Product);

export const query = graphql`
    query($id: String!) {
        merlinWebsite {
            websiteId
            store {
                currency {
                    symbol
                    code
                }
            }
        }
        merlinStoreProduct(_id: { eq: $id }) {
            _id
            name
            shortName
            description
            price
            category {
                _id
                name
            }
            images {
                image: url {
                    childImageSharp {
                        fluid(maxHeight: 600) {
                            ...GatsbyImageSharpFluid
                        }
                    }
                }
            }
            discounts {
                _id
                type
                code
                type
                amount
                startDate
                endDate
                limit
                products
                categories
                accountTypes
            }
            customFields {
                _id
                type
                label
                id
                multiple
                value {
                    string
                    strings
                    paragraph
                    paragraphs
                    html
                    htmls
                    dropdown
                    dropdowns
                    radio
                    checkbox
                    image {
                        image: url {
                            childImageSharp {
                                fluid(maxWidth: 1200) {
                                    ...GatsbyImageSharpFluid
                                }
                            }
                        }
                    }
                    images {
                        image: url {
                            childImageSharp {
                                fluid(maxWidth: 1200) {
                                    ...GatsbyImageSharpFluid
                                }
                            }
                        }
                    }
                    switch
                }
                items {
                    _id
                    type
                    label
                    id
                    value {
                        string
                        strings
                        paragraph
                        paragraphs
                        html
                        htmls
                        dropdown
                        dropdowns
                        radio
                        checkbox
                        image {
                            image: url {
                                childImageSharp {
                                    fluid(maxWidth: 1200) {
                                        ...GatsbyImageSharpFluid
                                    }
                                }
                            }
                        }
                        images {
                            image: url {
                                childImageSharp {
                                    fluid(maxWidth: 1200) {
                                        ...GatsbyImageSharpFluid
                                    }
                                }
                            }
                        }
                        switch
                    }
                }
            }
        }
        allMerlinStoreProduct {
            nodes {
                _id
                name
                category {
                    _id
                }
                shortName
                description
                price
                images {
                    image: url {
                        childImageSharp {
                            fluid(maxWidth: 600) {
                                ...GatsbyImageSharpFluid
                            }
                        }
                    }
                }
            }
        }
        allMerlinAccountType {
            nodes {
                _id
                merlinId
                name
            }
        }
    }
`;
